import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AlertCountResponse,
  AlertListResponse,
  AlertOrderOptions,
  AlertQueryOptions,
  AlertWithUser,
  UpdateAlertRequest,
} from "../Types/alert";
import useFetch from "./useFetchAPI";

type AlertQueryState = AlertQueryOptions & {
  limit: number;
  offset: number;
  orderBy: AlertOrderOptions;
};

const DEFAULT_QUERY_STATE: AlertQueryState = {
  limit: 25,
  offset: 0,
  orderBy: {
    field: "LastUpdatedAt",
    direction: "DESC",
  },
};

const sanitizeQueryValue = (value: unknown) => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === "string" && value.trim() === "") {
    return undefined;
  }

  return value;
};

const buildQueryString = (
  options: AlertQueryState,
  includePaginationAndOrdering = true
) => {
  const params = new URLSearchParams();

  const keys = Object.keys(options) as Array<keyof AlertQueryState>;
  for (const key of keys) {
    if (!includePaginationAndOrdering && (key === "limit" || key === "offset" || key === "orderBy")) {
      continue;
    }

    const value = options[key];
    if (value === undefined) {
      continue;
    }

    appendQueryParam(params, key, value, includePaginationAndOrdering);
  } 

  const query = params.toString();
  return query ? `?${query}` : "";
};

const appendQueryParam = (
  params: URLSearchParams,
  key: keyof AlertQueryState,
  value: unknown,
  includePaginationAndOrdering: boolean
) => {
  if (key === "limit" || key === "offset") {
    params.append(key, String(value));
  } else if (key === "IsPublic") {
    params.append(key, (value as boolean) ? "true" : "false");
  } else if (key === "CreatedByName" || key === "ThresholdValueMin" || key === "ThresholdValueMax") {
    params.append(key, String(value));
  } else if (isDateField(key)) {
    params.append(key, (value as Date).toISOString());
  } else if (key === "orderBy" && includePaginationAndOrdering && value) {
    const orderBy = value as AlertOrderOptions;
    params.append("orderByField", orderBy.field);
    params.append("orderByDirection", orderBy.direction);
  } else {
    params.append(key, String(value));
  }
};

const isDateField = (key: string): key is "DateFrom" | "DateTo" | "LastTriggeredFrom" | "LastTriggeredTo" | "LastNotificationSentFrom" | "LastNotificationSentTo" => {
  return ["DateFrom", "DateTo", "LastTriggeredFrom", "LastTriggeredTo", "LastNotificationSentFrom", "LastNotificationSentTo"].includes(key);
};

const useAlertManagement = (initialState: Partial<AlertQueryState> = {}) => {
  const queryRef = useRef<AlertQueryState>({
    ...DEFAULT_QUERY_STATE,
    ...initialState,
    orderBy: initialState.orderBy ?? DEFAULT_QUERY_STATE.orderBy,
    limit: initialState.limit ?? DEFAULT_QUERY_STATE.limit,
    offset: initialState.offset ?? DEFAULT_QUERY_STATE.offset,
  });

  const [queryState, setQueryState] = useState<AlertQueryState>(
    queryRef.current
  );
  const [alerts, setAlerts] = useState<AlertWithUser[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const {
    fetchAPI: fetchAlertsAPI,
    apiData: alertsData,
    isLoading: isLoadingAlerts,
    serverError: alertsError,
  } = useFetch({ scope: "AppUser" });

  const {
    fetchAPI: fetchCountAPI,
    apiData: countData,
    isLoading: isLoadingCount,
    serverError: countError,
  } = useFetch({ scope: "AppUser" });

  const {
    fetchAPI: updateAlertAPI,
    isLoading: isUpdating,
    serverError: updateError,
  } = useFetch({ scope: "AppUser" });

  const {
    fetchAPI: updateAlertStatusAPI,
    isLoading: isUpdatingStatus,
    serverError: updateStatusError,
  } = useFetch({ scope: "AppUser" });

  const {
    fetchAPI: deleteAlertAPI,
    isLoading: isDeleting,
    serverError: deleteError,
  } = useFetch({ scope: "AppUser" });

  const loadAlerts = useCallback(
    async (state: AlertQueryState) => {
      const queryString = buildQueryString(state, true);
      await fetchAlertsAPI({ url: `alerts/all${queryString}`, method: "GET" });
    },
    [fetchAlertsAPI]
  );

  const loadCount = useCallback(
    async (state: AlertQueryState) => {
      const queryString = buildQueryString(state, false);
      await fetchCountAPI({ url: `alerts/count${queryString}`, method: "GET" });
    },
    [fetchCountAPI]
  );

  const updateQueryState = useCallback(
    async (
      overrides?: Partial<AlertQueryState>,
      options?: { resetOffset?: boolean }
    ) => {
      const current = queryRef.current;
      const next: AlertQueryState = {
        ...current,
        ...(options?.resetOffset ? { offset: 0 } : {}),
        ...overrides,
        orderBy: overrides?.orderBy ?? current.orderBy,
        limit: overrides?.limit ?? current.limit,
        offset: options?.resetOffset ? 0 : overrides?.offset ?? current.offset,
      };

      if (overrides) {
        for (const [key, value] of Object.entries(overrides)) {
          if (key === "limit" || key === "offset" || key === "orderBy") {
            continue;
          }

          const sanitized = sanitizeQueryValue(value);
          if (sanitized === undefined) {
            delete (next as any)[key];
          } else {
            (next as any)[key] = sanitized;
          }
        }
      }

      queryRef.current = next;
      setQueryState(next);

      await Promise.all([loadAlerts(next), loadCount(next)]);
    },
    [loadAlerts, loadCount]
  );

  useEffect(() => {
    void updateQueryState();
  }, []);

  useEffect(() => {
    if (!isLoadingAlerts && alertsData) {
      const typed = alertsData as unknown as AlertListResponse;
      if (Array.isArray(typed.alerts)) {
        setAlerts(typed.alerts);
      } else {
        setAlerts([]);
      }
    }
  }, [alertsData, isLoadingAlerts]);

  useEffect(() => {
    if (!isLoadingCount && countData) {
      const typed = countData as unknown as AlertCountResponse;
      if (typeof typed.count === "number") {
        setTotalCount(typed.count);
      }
    }
  }, [countData, isLoadingCount]);

  const updateFilters = useCallback(
    async (filters: Partial<AlertQueryOptions>) => {
      await updateQueryState(filters as Partial<AlertQueryState>, {
        resetOffset: true,
      });
    },
    [updateQueryState]
  );

  const setPage = useCallback(
    async (page: number) => {
      const sanitizedPage = Math.max(1, page);
      const { limit } = queryRef.current;
      await updateQueryState({ offset: (sanitizedPage - 1) * limit });
    },
    [updateQueryState]
  );

  const setPageSize = useCallback(
    async (pageSize: number) => {
      const sanitizedSize = Math.max(5, pageSize);
      await updateQueryState({ limit: sanitizedSize, offset: 0 });
    },
    [updateQueryState]
  );

  const updateSorting = useCallback(
    async (orderBy: AlertOrderOptions) => {
      await updateQueryState({ orderBy });
    },
    [updateQueryState]
  );

  const refresh = useCallback(async () => {
    await updateQueryState();
  }, [updateQueryState]);

  const handleUpdateAlert = useCallback(
    async (payload: UpdateAlertRequest) => {
      await updateAlertAPI({
        url: "alerts/update",
        method: "PUT",
        body: payload as unknown as Record<string, unknown>,
      });
      await refresh();
    },
    [refresh, updateAlertAPI]
  );

  const handleUpdateAlertStatus = useCallback(
    async (alertId: number, isActive: boolean) => {
      await updateAlertStatusAPI({
        url: "alerts/update-status",
        method: "PUT",
        body: { AlertId: alertId, isActive } as unknown as Record<
          string,
          unknown
        >,
      });
      await refresh();
    },
    [updateAlertStatusAPI, refresh]
  );

  const handleDeleteAlert = useCallback(
    async (alertId: number) => {
      await deleteAlertAPI({
        url: "alerts/delete",
        method: "DELETE",
        body: { AlertId: alertId } as unknown as Record<string, unknown>,
      });

      const current = queryRef.current;
      const shouldPageBackward =
        alerts.length <= 1 && current.offset >= current.limit;
      if (shouldPageBackward) {
        await updateQueryState({ offset: current.offset - current.limit });
      } else {
        await refresh();
      }
    },
    [alerts.length, deleteAlertAPI, refresh, updateQueryState]
  );

  const isMutating = isUpdating || isUpdatingStatus || isDeleting;

  const combinedError = useMemo(() => {
    return (
      alertsError ||
      countError ||
      updateError ||
      updateStatusError ||
      deleteError ||
      null
    );
  }, [alertsError, countError, updateError, updateStatusError, deleteError]);

  const currentPage = useMemo(() => {
    return Math.floor(queryState.offset / queryState.limit) + 1;
  }, [queryState.limit, queryState.offset]);

  const totalPages = useMemo(() => {
    if (queryState.limit === 0) {
      return 1;
    }

    return Math.max(1, Math.ceil(totalCount / queryState.limit));
  }, [queryState.limit, totalCount]);

  return {
    alerts,
    totalCount,
    queryState,
    currentPage,
    totalPages,
    isLoading: isLoadingAlerts || isLoadingCount || isMutating,
    error: combinedError,
    updateFilters,
    setPage,
    setPageSize,
    updateSorting,
    refresh,
    updateAlert: handleUpdateAlert,
    updateAlertStatus: handleUpdateAlertStatus,
    deleteAlert: handleDeleteAlert,
  };
};

export default useAlertManagement;
